import { useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';

import { Accordion, AccordionValue } from '@/components/Accordion';

import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { classNames } from '@/utils/classnames';
import { routes } from '@/utils/routes';

import { sectionTitleClass } from '../shared-styles';

const yearClass = classNames(
  'absolute',
  'left-0',
  '-mt-2.5',
  'text-token-gray-700',
  'font-body',
  'text-400',
  'leading-6',
  'font-semibold',
  'transition-all',
  'duration-500',
  'tablet:translate-x-[20px]',
  'tablet:[[data-state=open]_&]:translate-x-[40px]',
  'tablet:[[data-state=open]_&]:text-token-gray-1200',
  'tablet:[[data-state=open]_&]:before:-translate-x-[40px]',
  'tablet:[[data-state=open]_&]:before:w-7.5',
  "tablet:before:content-['']",
  'tablet:before:absolute',
  'tablet:before:top-1/2',
  'tablet:before:h-px',
  'tablet:before:w-7.5',
  'tablet:before:bg-[rgb(2,32,110)]',
  'tablet:before:-translate-x-[40px]',
  'tablet:before:origin-left',
  'tablet:data-[state=closed]:before:opacity-50',
);

export const OurStorySection = () => {
  const isDesktop = useMedia(breakpoints.desktop, true);
  const [selectedYear, setYear] = useState<AccordionValue>('2017');
  const [height, setHeight] = useState(0);
  const accordionRef = useRef<HTMLDivElement>(null);

  /**
   * Sets the height of the section based on the content inside the accordion.
   * The height is adjusted based on the device type (desktop or mobile).
   * This is needed to avoid the section height changing when the accordion is expanded.
   */
  useEffect(() => {
    function setSectionHeight() {
      const increment = isDesktop ? 100 : 250;
      if (accordionRef.current) {
        const containerHeight = accordionRef.current.clientHeight ?? 0;
        setHeight(containerHeight + increment);
      }
    }

    setSectionHeight();
    window.addEventListener('resize', setSectionHeight);

    return () => {
      window.removeEventListener('resize', setSectionHeight);
    };
  }, [isDesktop]);

  return (
    <section
      className="mx-auto max-w-[1240px] pt-10 pb-10 pl-6 tablet:pl-0 pr-6 flex flex-col bg-token-gray-100 rounded-[36px] border border-solid border-token-gray-300 mb-20 overflow-hidden tablet:bg-[url(/images/about-us/0x-logo-background.svg)] tablet:bg-no-repeat tablet:bg-right-bottom desktop:flex-row-reverse desktop:gap-[114px]"
      style={{ height }}
    >
      <div className="tablet:pl-[120px] desktop:w-[40%] desktop:pl-0 desktop:pr-[150px]">
        <h1 className={sectionTitleClass}>Our story</h1>
        <div
          className={fontStyles({
            variant: 'BODY_LG',
            class: ['text-token-gray-800', 'mt-7', 'max-w-[31rem]', 'leading-6'],
          })}
        >
          Founded in 2020 by DEX infrastructure provider{' '}
          <a
            href={routes.ZEROEX_HOME}
            className={linkStyles({ underline: true, color: 'purple' })}
            target="_blank"
          >
            0x
          </a>
          , Matcha is the most frictionless experience for searching and trading tokens. Search,
          trade, done!
        </div>
      </div>
      <div className="flex-1 mt-10 desktop:mt-0 tablet:mr-[280px] desktop:mr-0">
        <Accordion.Root
          ref={accordionRef}
          type="single"
          defaultValue="2017"
          className="relative flex flex-col gap-4"
          onValueChange={setYear}
        >
          <Accordion.Item
            className={classNames(
              'rounded-md',
              'border',
              'border-token-gray-300',
              'p-6',
              'pr-8',
              'font-body',
              'ml-16',
              'tablet:ml-23',
              'shadow-modal-badge',
              'data-[state=open]:pr-9.5',
              'data-[state=open]:bg-primitive-basic-white',
              'data-[state=closed]:p-0',
              '[-webkit-tap-highlight-color:transparent]',
              selectedYear !== '2017' ? 'opacity-50' : undefined,
            )}
            value="2017"
          >
            <Accordion.Trigger
              className={classNames(
                'border-0',
                'grid',
                'w-full',
                'bg-transparent',
                'p-0',
                'gap-2.5',
                '[grid-template-rows:repeat(3,1fr)]',
                'justify-items-start',
                'items-center',
                'tablet:[grid-template-columns:6fr_6fr_5fr]',
                'tablet:[grid-template-rows:1fr]',
                'tablet:[[data-state="open"]_&]:[grid-template-columns:repeat(2,1fr)]',
                'tablet:[[data-state="open"]_&]:[grid-template-rows:repeat(2,1fr)]',
                '[[data-state="closed"]_&]:p-6',
                '[[data-state="closed"]_&]:pb-10',
                '[[data-state="closed"]_&]:pr-8',
              )}
              showChevron={false}
            >
              <span className={yearClass}>2017</span>
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: ['text-token-gray-1600', 'order-1'],
                })}
              >
                0 Wallets
              </span>
              <span
                className={fontStyles({
                  variant: 'LABEL_LG',
                  class: [
                    'text-token-gray-800',
                    'order-3',
                    'tablet:[[data-state="closed"]_&]:order-2',
                  ],
                })}
              >
                0x Foundation
              </span>
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: [
                    'text-token-gray-1200',
                    'order-2',
                    'mt-1',
                    'tablet:mt-0',
                    'tablet:[[data-state="open"]_&]:justify-self-end',
                    'tablet:[[data-state="closed"]_&]:order-3',
                    'tablet:[[data-state="closed"]_&]:font-normal',
                  ],
                })}
              >
                Foundation
              </span>
            </Accordion.Trigger>
            <Accordion.Content
              className={fontStyles({
                variant: 'BODY_LG',
                class: ['text-token-gray-800', 'mt-3', 'p-0'],
              })}
            >
              0x was launched in 2017 with a mission to build a tokenized world where all value can
              flow freely. Providing decentralized tools for market makers and takers to interact,
              0x infra quickly becomes the backbone of DeFi.
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item
            className={classNames(
              'rounded-md',
              'border',
              'border-token-gray-300',
              'p-6',
              'pr-8',
              'font-body',
              'ml-16',
              'tablet:ml-23',
              'shadow-modal-badge',
              'data-[state=open]:pr-9.5',
              'data-[state=open]:bg-primitive-basic-white',
              'data-[state=closed]:p-0',
              '[-webkit-tap-highlight-color:transparent]',
              selectedYear !== '2020' ? 'opacity-50' : undefined,
            )}
            value="2020"
          >
            <Accordion.Trigger
              className={classNames(
                'border-0',
                'grid',
                'w-full',
                'bg-transparent',
                'p-0',
                'gap-2.5',
                '[grid-template-rows:repeat(3,1fr)]',
                'justify-items-start',
                'items-center',
                'tablet:[grid-template-columns:6fr_6fr_5fr]',
                'tablet:[grid-template-rows:1fr]',
                'tablet:[[data-state="open"]_&]:[grid-template-columns:repeat(2,1fr)]',
                'tablet:[[data-state="open"]_&]:[grid-template-rows:repeat(2,1fr)]',
                '[[data-state="closed"]_&]:p-6',
                '[[data-state="closed"]_&]:pb-10',
                '[[data-state="closed"]_&]:pr-8',
              )}
              showChevron={false}
            >
              <span className={yearClass}>2020</span>
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: ['text-token-gray-1600', 'order-1'],
                })}
              >
                200k Wallets
              </span>
              <span
                className={fontStyles({
                  variant: 'LABEL_LG',
                  class: [
                    'text-token-gray-800',
                    'order-3',
                    'tablet:[[data-state="closed"]_&]:order-2',
                  ],
                })}
              >
                Seed round
              </span>
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: [
                    'text-token-gray-1200',
                    'order-2',
                    'mt-1',
                    'tablet:mt-0',
                    'tablet:[[data-state="open"]_&]:justify-self-end',
                    'tablet:[[data-state="closed"]_&]:order-3',
                    'tablet:[[data-state="closed"]_&]:font-normal',
                  ],
                })}
              >
                Matcha v1
              </span>
            </Accordion.Trigger>
            <Accordion.Content
              className={fontStyles({
                variant: 'BODY_LG',
                class: ['text-token-gray-800', 'mt-3', 'p-0'],
              })}
            >
              Matcha launched in April 2020 as a better way to swap Ethereum tokens, aggregating
              liquidity from 15 DEXs and innovating Smart Order Routing.
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item
            className={classNames(
              'rounded-md',
              'border',
              'border-token-gray-300',
              'p-6',
              'pr-8',
              'font-body',
              'ml-16',
              'tablet:ml-23',
              'shadow-modal-badge',
              'data-[state=open]:pr-9.5',
              'data-[state=open]:bg-primitive-basic-white',
              'data-[state=closed]:p-0',
              '[-webkit-tap-highlight-color:transparent]',
              selectedYear !== '2021' ? 'opacity-50' : undefined,
            )}
            value="2021"
          >
            <Accordion.Trigger
              className={classNames(
                'border-0',
                'grid',
                'w-full',
                'bg-transparent',
                'p-0',
                'gap-2.5',
                '[grid-template-rows:repeat(3,1fr)]',
                'justify-items-start',
                'items-center',
                'tablet:[grid-template-columns:6fr_6fr_5fr]',
                'tablet:[grid-template-rows:1fr]',
                'tablet:[[data-state="open"]_&]:[grid-template-columns:repeat(2,1fr)]',
                'tablet:[[data-state="open"]_&]:[grid-template-rows:repeat(2,1fr)]',
                '[[data-state="closed"]_&]:p-6',
                '[[data-state="closed"]_&]:pb-10',
                '[[data-state="closed"]_&]:pr-8',
              )}
              showChevron={false}
            >
              <span className={yearClass}>2021</span>
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: ['text-token-gray-1600', 'order-1'],
                })}
              >
                400k Wallets
              </span>
              <span
                className={fontStyles({
                  variant: 'LABEL_LG',
                  class: [
                    'text-token-gray-800',
                    'order-3',
                    'tablet:[[data-state="closed"]_&]:order-2',
                  ],
                })}
              >
                Series A
              </span>
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: [
                    'text-token-gray-1200',
                    'order-2',
                    'mt-1',
                    'tablet:mt-0',
                    'tablet:[[data-state="open"]_&]:justify-self-end',
                    'tablet:[[data-state="closed"]_&]:order-3',
                    'tablet:[[data-state="closed"]_&]:font-normal',
                  ],
                })}
              >
                Matcha v2
              </span>
            </Accordion.Trigger>
            <Accordion.Content
              className={fontStyles({
                variant: 'BODY_LG',
                class: ['text-token-gray-800', 'mt-3', 'p-0'],
              })}
            >
              Version 2 brought a multi-chain experience with support for Ethereum, BSC, and
              Polygon. Matcha OTC product is introduced as a predecessor to RFQ on Matcha today.
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item
            className={classNames(
              'rounded-md',
              'border',
              'border-token-gray-300',
              'p-6',
              'pr-8',
              'font-body',
              'ml-16',
              'tablet:ml-23',
              'shadow-modal-badge',
              'data-[state=open]:pr-9.5',
              'data-[state=open]:bg-primitive-basic-white',
              'data-[state=closed]:p-0',
              '[-webkit-tap-highlight-color:transparent]',
              selectedYear !== '2023' ? 'opacity-50' : undefined,
            )}
            value="2023"
          >
            <Accordion.Trigger
              className={classNames(
                'border-0',
                'grid',
                'w-full',
                'bg-transparent',
                'p-0',
                'gap-2.5',
                '[grid-template-rows:repeat(3,1fr)]',
                'justify-items-start',
                'items-center',
                'tablet:[grid-template-columns:6fr_6fr_5fr]',
                'tablet:[grid-template-rows:1fr]',
                'tablet:[[data-state="open"]_&]:[grid-template-columns:repeat(2,1fr)]',
                'tablet:[[data-state="open"]_&]:[grid-template-rows:repeat(2,1fr)]',
                '[[data-state="closed"]_&]:p-6',
                '[[data-state="closed"]_&]:pb-10',
                '[[data-state="closed"]_&]:pr-8',
              )}
              showChevron={false}
            >
              <span className={yearClass}>2023</span>
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: ['text-token-gray-1600', 'order-1'],
                })}
              >
                500k Wallets
              </span>
              <span
                className={fontStyles({
                  variant: 'LABEL_LG',
                  class: [
                    'text-token-gray-800',
                    'order-3',
                    'tablet:[[data-state="closed"]_&]:order-2',
                  ],
                })}
              >
                Series B
              </span>
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: [
                    'text-token-gray-1200',
                    'order-2',
                    'mt-1',
                    'tablet:mt-0',
                    'tablet:[[data-state="open"]_&]:justify-self-end',
                    'tablet:[[data-state="closed"]_&]:order-3',
                    'tablet:[[data-state="closed"]_&]:font-normal',
                  ],
                })}
              >
                Matcha v3
              </span>
            </Accordion.Trigger>
            <Accordion.Content
              className={fontStyles({
                variant: 'BODY_LG',
                class: ['text-token-gray-800', 'mt-3', 'p-0'],
              })}
            >
              Over 5 million tokens on 9 networks - and growing! Matcha v3 offers the lowest trade
              failure rates in DeFi and adds Matcha Auto for MEV protection, gasless swaps, and
              optimal trade execution.
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      </div>
    </section>
  );
};
